import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supplierReconSessionsAPI, supplierReconAPI } from '../lib/api';
import NewReconSessionModal from '../components/NewReconSessionModal';
import {
  FiPackage, FiPlus, FiTrash2, FiLoader, FiAlertCircle,
  FiCheckCircle, FiAlertTriangle, FiXCircle, FiClock,
  FiRefreshCw, FiCalendar, FiFileText, FiX, FiSearch, FiFilter,
} from 'react-icons/fi';

function DeleteConfirmModal({ session, onConfirm, onCancel, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <FiTrash2 className="w-5 h-5" />
            <h2 className="text-base font-semibold">Delete Recon Session</h2>
          </div>
          <button onClick={onCancel} disabled={deleting} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700">
            You are about to permanently delete <span className="font-semibold">"{session.name}"</span>.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 space-y-1">
            <p className="font-medium">This will permanently delete:</p>
            <ul className="list-disc list-inside space-y-0.5 text-red-700 mt-1">
              <li>The recon session and all its settings</li>
              <li>All {session.doc_count || 0} synced document records</li>
              <li>All AI extraction results and supplier data</li>
              <li>All {session.match_count || 0} supplier matches and reports</li>
            </ul>
            <p className="mt-2 font-medium">This action cannot be undone.</p>
          </div>
          <p className="text-xs text-gray-500">
            Note: Original files in SharePoint or Exchange are not affected — only the recon data stored in Doxolve is deleted.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {deleting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />}
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_CONFIG = {
  pending: { icon: FiClock, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Pending' },
  syncing: { icon: FiRefreshCw, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Syncing', spin: true },
  processing: { icon: FiLoader, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Processing', spin: true },
  matching: { icon: FiLoader, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Matching', spin: true },
  in_progress: { icon: FiClock, color: 'text-amber-600', bg: 'bg-amber-100', label: 'In Progress' },
  complete: { icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Complete' },
  error: { icon: FiXCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Error' },
};

function StatusBadge({ status, reconAccepted }) {
  const effectiveStatus = getDisplayStatus({ status, recon_accepted: reconAccepted });
  const cfg = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function formatPeriod(start, end) {
  const s = new Date(start).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  const e = new Date(end).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
}

const PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'syncing', label: 'Syncing' },
  { value: 'processing', label: 'Processing' },
  { value: 'matching', label: 'Matching' },
  { value: 'error', label: 'Error' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'complete', label: 'Complete' },
];
const STATUS_FILTER_VALUES = STATUS_FILTER_OPTIONS.map(o => o.value);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Status as the user sees it on the card badge (a finished run is only "Complete" once accepted)
function getDisplayStatus(session) {
  return session.status === 'complete' && !session.recon_accepted ? 'in_progress' : session.status;
}

function matchesStatus(session, value) {
  return !value || getDisplayStatus(session) === value;
}

// Compare YYYY-MM-DD strings only (no Date parsing, so no timezone drift)
function dayOf(v) {
  return v ? String(v).slice(0, 10) : '';
}

function readFilters(params) {
  const status = params.get('status') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';
  return {
    q: params.get('q') || '',
    group: params.get('group') || '',
    status: STATUS_FILTER_VALUES.includes(status) ? status : '',
    from: DATE_RE.test(from) ? from : '',
    to: DATE_RE.test(to) ? to : '',
  };
}

const FILTER_PARAM_KEYS = ['q', 'group', 'status', 'from', 'to'];
const FOCUS_RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-1';

export default function SupplierRecon() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // session object to confirm delete
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Search + filters (kept in the URL: q, group, status, from, to)
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(() => readFilters(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [searchInput, setSearchInput] = useState(initial.q);
  const [searchText, setSearchText] = useState(initial.q);
  const [filterGroup, setFilterGroup] = useState(initial.group);
  const [filterStatus, setFilterStatus] = useState(initial.status);
  const [filterFrom, setFilterFrom] = useState(initial.from);
  const [filterTo, setFilterTo] = useState(initial.to);
  const [showFilters, setShowFilters] = useState(!!(initial.group || initial.status || initial.from || initial.to));
  const [groups, setGroups] = useState([]);
  const [supplierResult, setSupplierResult] = useState(null); // { term, ids: Set, truncated }
  const [liveCount, setLiveCount] = useState(''); // debounced result-count text (announced politely)
  const searchInputRef = useRef(null);
  const [supplierSearching, setSupplierSearching] = useState(false);
  const [supplierSearchFailed, setSupplierSearchFailed] = useState(false);
  const supplierReqId = useRef(0);
  const supplierAbort = useRef(null);
  const lastWrittenParams = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      setError(null);
      const res = await supplierReconSessionsAPI.getSessions();
      setSessions(res.data.sessions || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Recon groups for the group filter
  useEffect(() => {
    let cancelled = false;
    supplierReconAPI.getGroups()
      .then(res => { if (!cancelled) setGroups(res.data?.groups || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Debounce the search box (~300ms)
  useEffect(() => {
    if (searchInput === searchText) return undefined;
    const t = setTimeout(() => setSearchText(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, searchText]);

  // Write filters to the URL (replace, so the back button is not polluted)
  useEffect(() => {
    const wanted = { q: searchText.trim(), group: filterGroup, status: filterStatus, from: filterFrom, to: filterTo };
    const next = new URLSearchParams(searchParams);
    let changed = false;
    FILTER_PARAM_KEYS.forEach(k => {
      const cur = next.get(k) || '';
      if (cur !== wanted[k]) {
        changed = true;
        if (wanted[k]) next.set(k, wanted[k]); else next.delete(k);
      }
    });
    if (!changed) return;
    lastWrittenParams.current = next.toString();
    setSearchParams(next, { replace: true });
  }, [searchText, filterGroup, filterStatus, filterFrom, filterTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // External URL change (e.g. sidebar link to /supplier-recon with no params): follow the URL
  useEffect(() => {
    if (lastWrittenParams.current !== null && searchParams.toString() === lastWrittenParams.current) return;
    const f = readFilters(searchParams);
    setSearchInput(f.q);
    setSearchText(f.q);
    setFilterGroup(f.group);
    setFilterStatus(f.status);
    setFilterFrom(f.from);
    setFilterTo(f.to);
    if (f.group || f.status || f.from || f.to) setShowFilters(true);
    // Remember the URL we just followed, so a later Back to a URL we wrote earlier
    // is recognised as an external change too and re-applies its filters.
    lastWrittenParams.current = searchParams.toString();
  }, [searchParams]);

  // Supplier-name search (server side) when the debounced term has 2+ characters
  useEffect(() => {
    const term = searchText.trim();
    if (supplierAbort.current) supplierAbort.current.abort();
    supplierAbort.current = null;
    if (term.length < 2) {
      supplierReqId.current += 1;
      setSupplierResult(null);
      setSupplierSearching(false);
      setSupplierSearchFailed(false);
      return undefined;
    }
    const reqId = ++supplierReqId.current;
    const controller = new AbortController();
    supplierAbort.current = controller;
    setSupplierSearching(true);
    setSupplierSearchFailed(false);
    supplierReconSessionsAPI.searchSessionIds(term, { signal: controller.signal })
      .then(res => {
        if (reqId !== supplierReqId.current) return;
        setSupplierResult({ term, ids: new Set(res.data?.session_ids || []), truncated: !!res.data?.truncated });
        setSupplierSearching(false);
      })
      .catch(err => {
        if (reqId !== supplierReqId.current) return;
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setSupplierResult(null);
        setSupplierSearching(false);
        setSupplierSearchFailed(true);
      });
    return () => { controller.abort(); };
  }, [searchText]);

  // Any filter change returns to the first page of results
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchText, filterGroup, filterStatus, filterFrom, filterTo, supplierResult]);

  // Auto-refresh when any session is in-progress
  useEffect(() => {
    const inProgress = sessions.some(s => ['syncing', 'processing', 'matching'].includes(s.status));
    if (!inProgress) return;
    const timer = setInterval(loadSessions, 5000);
    return () => clearInterval(timer);
  }, [sessions, loadSessions]);

  const handleCreated = (session) => {
    setShowModal(false);
    navigate(`/supplier-recon/${session.id}`);
  };

  const handleDeleteClick = (e, session) => {
    e.stopPropagation();
    setConfirmDelete(session);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(confirmDelete.id);
      await supplierReconSessionsAPI.deleteSession(confirmDelete.id);
      setSessions(prev => prev.filter(s => s.id !== confirmDelete.id));
      setConfirmDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(null);
    }
  };

  const activeCount = sessions.filter(s => ['syncing', 'processing', 'matching'].includes(s.status)).length;

  const activeFilterCount = [filterGroup, filterStatus, filterFrom, filterTo].filter(Boolean).length;
  const hasSearch = searchInput.trim().length > 0;
  const hasActiveFilters = activeFilterCount > 0 || hasSearch;
  const invalidRange = !!(filterFrom && filterTo && filterFrom > filterTo);

  // Group options: API groups plus any group only seen on a loaded run
  const groupOptions = useMemo(() => {
    const map = new Map();
    sessions.forEach(s => { if (s.group_id && s.group_name) map.set(s.group_id, s.group_name); });
    groups.forEach(g => { if (g.id) map.set(g.id, g.name); });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [groups, sessions]);

  const filteredSessions = useMemo(() => {
    if (invalidRange) return [];
    const trimmed = searchText.trim();
    const term = trimmed.toLowerCase();
    // Use the finished supplier search for this term. While a search for the same or a longer
    // term is in flight, keep the previous supplier matches so the list does not jump.
    let supplierIds = null;
    if (supplierResult) {
      if (supplierResult.term === trimmed) supplierIds = supplierResult.ids;
      else if (trimmed.length >= 2 && term.includes(supplierResult.term.toLowerCase())) supplierIds = supplierResult.ids;
    }
    return sessions.filter(s => {
      if (term) {
        const nameHit = (s.name || '').toLowerCase().includes(term) || (s.group_name || '').toLowerCase().includes(term);
        const supplierHit = !!(supplierIds && supplierIds.has(s.id));
        if (!nameHit && !supplierHit) return false;
      }
      if (filterGroup && s.group_id !== filterGroup) return false;
      if (!matchesStatus(s, filterStatus)) return false;
      // Period overlap: run ends on/after "from" and starts on/before "to"
      if (filterFrom) { const end = dayOf(s.period_end); if (end && end < filterFrom) return false; }
      if (filterTo) { const start = dayOf(s.period_start); if (start && start > filterTo) return false; }
      return true;
    });
  }, [sessions, searchText, supplierResult, filterGroup, filterStatus, filterFrom, filterTo, invalidRange]);

  const clearSearch = () => { setSearchInput(''); setSearchText(''); };
  const clearFilters = () => {
    setSearchInput('');
    setSearchText('');
    setFilterGroup('');
    setFilterStatus('');
    setFilterFrom('');
    setFilterTo('');
    // The button that was clicked unmounts, so hand focus to the search box
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // Result-count line (inside the single polite live region below)
  const matchingCount = filteredSessions.length;
  const shownCount = Math.min(matchingCount, visibleCount);
  let countText = '';
  if (hasActiveFilters) {
    if (matchingCount === 0) countText = 'No runs match your filters';
    else if (shownCount === matchingCount) countText = `${matchingCount} of ${sessions.length} runs match`;
    else countText = `Showing ${shownCount} of ${matchingCount} matching runs (${sessions.length} total)`;
  }

  // Only publish the count once things settle (typing debounce done, supplier search finished),
  // so typing does not re-announce on every keystroke
  const typingSettled = searchInput.trim() === searchText.trim();
  useEffect(() => {
    if (!countText) { setLiveCount(''); return undefined; }
    if (supplierSearching || !typingSettled) return undefined;
    const t = setTimeout(() => setLiveCount(countText), 400);
    return () => clearTimeout(t);
  }, [countText, supplierSearching, typingSettled]);

  const supplierTruncated = !!(supplierResult && supplierResult.truncated && supplierResult.term === searchText.trim() && !supplierSearching);

  const fieldClass = 'w-full text-sm border border-gray-300 rounded-lg px-2 py-2 min-h-[44px] sm:min-h-0 sm:py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiPackage className="w-7 h-7 text-green-600" />
            Supplier Recon
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Financial supplier reconciliation — invoices, statements, payments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadSessions}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Recon Run
          </button>
        </div>
      </div>

      {/* Active count notice */}
      {activeCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-sm text-blue-800">
          <FiLoader className="w-4 h-4 animate-spin flex-shrink-0" />
          {activeCount} recon run{activeCount > 1 ? 's' : ''} in progress. Auto-refreshing every 5 seconds.
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Search & filter panel */}
      {!loading && sessions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="recon-search"
                ref={searchInputRef}
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                aria-label="Search recon runs"
                placeholder="Search by run name, group or supplier…"
                className="w-full pl-9 pr-11 py-2 min-h-[44px] sm:min-h-0 text-sm border border-gray-300 rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="Clear search"
                  className={`absolute right-1 top-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1.5 rounded text-gray-500 hover:text-gray-700 ${FOCUS_RING}`}
                >
                  <FiX className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(f => !f)}
              aria-expanded={showFilters}
              aria-controls="recon-filters-panel"
              className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] sm:min-h-0 text-sm border rounded-lg transition-colors ${FOCUS_RING} ${showFilters || activeFilterCount > 0 ? 'border-green-500 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              <FiFilter className="w-3.5 h-3.5" aria-hidden="true" />
              Filters
              {activeFilterCount > 0 && (
                <span className="min-w-[1.25rem] text-center px-1.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-900 border border-green-300">
                  {activeFilterCount}
                  <span className="sr-only"> active</span>
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className={`inline-flex items-center min-h-[44px] sm:min-h-0 px-2 rounded text-xs text-gray-600 hover:text-gray-800 underline whitespace-nowrap ${FOCUS_RING}`}
              >
                Clear all
              </button>
            )}
          </div>

          {/* The one polite live region: search progress, notices and the result count */}
          <div id="recon-results-status" role="status" aria-live="polite" className="space-y-0.5">
            {supplierSearching && <p className="text-xs text-gray-600">Searching suppliers…</p>}
            {supplierSearchFailed && !supplierSearching && (
              <p className="text-xs text-gray-600">Supplier search is unavailable right now. Showing matches on run and group names only.</p>
            )}
            {supplierTruncated && (
              <p className="text-xs text-gray-600">Supplier search matched a lot of runs; narrow your search to see all of them.</p>
            )}
            {liveCount && <p className="text-xs text-gray-600 tabular-nums">{liveCount}</p>}
          </div>

          {/* Always rendered so assistive tech registers the live region before it is filled */}
          <p id="recon-range-hint" role="status" className={invalidRange ? 'text-xs text-amber-800' : 'sr-only'}>
            {invalidRange ? 'Period from is after Period to, so no runs can match. Adjust the dates.' : ''}
          </p>

          {showFilters && (
            <div id="recon-filters-panel" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-gray-100">
              <div>
                <label htmlFor="recon-filter-group" className="block text-xs text-gray-600 mb-1">Recon group</label>
                <select id="recon-filter-group" value={filterGroup} onChange={e => setFilterGroup(e.target.value)} className={fieldClass}>
                  <option value="">All groups</option>
                  {filterGroup && !groupOptions.some(g => g.id === filterGroup) && <option value={filterGroup}>Unknown group</option>}
                  {groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="recon-filter-status" className="block text-xs text-gray-600 mb-1">Status</label>
                <select id="recon-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={fieldClass}>
                  <option value="">All statuses</option>
                  {STATUS_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="recon-filter-from" className="block text-xs text-gray-600 mb-1">Period from</label>
                <input id="recon-filter-from" type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} aria-invalid={invalidRange} aria-describedby={invalidRange ? 'recon-range-hint' : undefined} className={fieldClass} />
              </div>
              <div>
                <label htmlFor="recon-filter-to" className="block text-xs text-gray-600 mb-1">Period to</label>
                <input id="recon-filter-to" type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} aria-invalid={invalidRange} aria-describedby={invalidRange ? 'recon-range-hint' : undefined} className={fieldClass} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sessions list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <FiLoader className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-medium text-gray-700 mb-1">No recon runs yet</h3>
          <p className="text-sm text-gray-500 mb-5 max-w-sm mx-auto">
            Create your first recon run to sync supplier documents and generate reconciliation reports.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Start First Recon Run
          </button>
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <FiSearch className="w-10 h-10 text-gray-400 mx-auto mb-3" aria-hidden="true" />
          <h3 className="text-base font-medium text-gray-700 mb-1">No recon runs match your filters</h3>
          <p className="text-sm text-gray-600 mb-4">Try a different search or adjust the filters.</p>
          <button
            type="button"
            onClick={clearFilters}
            className={`inline-flex items-center px-4 py-2 min-h-[44px] sm:min-h-0 text-sm font-medium text-green-800 border border-green-300 rounded-lg hover:bg-green-50 transition-colors ${FOCUS_RING}`}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.slice(0, visibleCount).map(session => {
            const inProgress = ['syncing', 'processing', 'matching'].includes(session.status);
            return (
              <div
                key={session.id}
                onClick={() => navigate(`/supplier-recon/${session.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-green-300 hover:shadow-sm transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    {session.group_name && (
                      <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-0.5">{session.group_name}</p>
                    )}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                        {session.name}
                      </h3>
                      <StatusBadge status={session.status} reconAccepted={session.recon_accepted} />
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <FiCalendar className="w-3.5 h-3.5" />
                        {formatPeriod(session.period_start, session.period_end)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiFileText className="w-3.5 h-3.5" />
                        {session.doc_count || 0} doc{session.doc_count !== 1 ? 's' : ''}
                      </span>
                      {session.match_count > 0 && (
                        <span className="flex items-center gap-1">
                          <FiCheckCircle className="w-3.5 h-3.5 text-green-500" />
                          {session.match_count} supplier{session.match_count !== 1 ? 's' : ''}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        Created {formatDateTime(session.created_at)}
                      </span>
                    </div>

                    {/* Sync summary */}
                    {session.sync_summary && Object.keys(session.sync_summary).length > 0 && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
                        {session.sync_summary.synced != null && (
                          <span className="text-green-600">{session.sync_summary.synced} synced</span>
                        )}
                        {session.sync_summary.failed > 0 && (
                          <span className="text-red-500">{session.sync_summary.failed} failed</span>
                        )}
                        {session.sync_summary.skipped > 0 && (
                          <span className="text-gray-400">{session.sync_summary.skipped} skipped</span>
                        )}
                      </div>
                    )}

                    {session.error_message && (
                      <p className="mt-1.5 text-xs text-red-600">{session.error_message}</p>
                    )}

                    {/* Progress bar for in-progress */}
                    {inProgress && (
                      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-400 rounded-full animate-pulse" style={{ width: '60%' }} />
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => handleDeleteClick(e, session)}
                      disabled={deleting === session.id || inProgress}
                      className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30"
                      title="Delete session"
                    >
                      {deleting === session.id
                        ? <FiLoader className="w-4 h-4 animate-spin" />
                        : <FiTrash2 className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {visibleCount < filteredSessions.length && (
            <div className="flex justify-center pt-2">
              <button
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                className="px-5 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
              >
                Load more ({filteredSessions.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </div>
      )}

      {/* New session modal */}
      {showModal && (
        <NewReconSessionModal
          onClose={() => setShowModal(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <DeleteConfirmModal
          session={confirmDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDelete(null)}
          deleting={deleting === confirmDelete.id}
        />
      )}
    </div>
  );
}
